import { FileRecord } from "@/shared/domain/files";
import { FileSystemNode, isFile } from "@/shared/domain/files/filesystem";

import { isPointCloud, pointCloudFileTypes } from "./pointCloud";
import { isUrdf, urdfFileTypes } from "./robotDescription";

/**
 * Files the platform can convert into a viewable glTF asset, articulated or
 * not.
 *
 * The union gates every route into the ingest wizard: the Ingest as Asset item,
 * and open-on-click too — row double click, grid tile, and the menu's View.
 * Viewing an ingestable source means seeing the model it converts to rather
 * than the bytes it is stored as, and that is as true of a point cloud scan as
 * of a robot description, so there is one question here rather than a wide one
 * for the menu and a narrow one for everything else.
 */
export const isIngestableAsAsset = (filename: string): boolean =>
  isUrdf(filename) || isPointCloud(filename);

/**
 * Extension list matching {@link isIngestableAsAsset}, for query-side
 * filtering.
 *
 * The model picker passes this to the file search so excluded files are never
 * paged in; deriving it from the two source lists keeps the two forms of the
 * gate from drifting apart.
 */
export const ingestableAssetFileTypes = [
  ...urdfFileTypes,
  ...pointCloudFileTypes,
];

/**
 * The file to open the ingest wizard for, or `null` when the node should be
 * opened the ordinary way (the file view for a file, navigation for a
 * directory).
 *
 * Every "open this node" entry point — row double click, grid tile click, the
 * row menu's View, and its Ingest as Asset item — asks this one question
 * instead of repeating `isFile(node) && isIngestableAsAsset(node.name)` and
 * risking the copies drifting apart.
 *
 * Returns the record rather than a boolean so the `isFile` narrowing survives
 * into the caller, which needs the {@link FileRecord} to open the wizard.
 */
export function assetSourceToIngest(node: FileSystemNode): FileRecord | null {
  return isFile(node) && isIngestableAsAsset(node.name) ? node.file : null;
}
